/**
 * PollControl - Modal de criação de enquetes (para o mestre)
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, BarChart3 } from 'lucide-react';
import { pollApi } from '../services/pollApi';

interface PollControlProps {
    tableId: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

export function PollControl({ tableId, isOpen, onOpenChange }: PollControlProps) {
    const [question, setQuestion] = useState('');
    const [options, setOptions] = useState<string[]>(['', '']);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [showResultsLive, setShowResultsLive] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddOption = () => {
        if (options.length < 10) {
            setOptions([...options, '']);
        } else {
            toast.error('Máximo de 10 opções permitido');
        }
    };

    const handleRemoveOption = (index: number) => {
        if (options.length > 2) {
            setOptions(options.filter((_, i) => i !== index));
        } else {
            toast.error('Mínimo de 2 opções necessário');
        }
    };

    const handleOptionChange = (index: number, value: string) => {
        const newOptions = [...options];
        newOptions[index] = value;
        setOptions(newOptions);
    };

    const handleSubmit = async () => {
        // Validações
        if (!question.trim()) {
            toast.error('Digite a pergunta da enquete');
            return;
        }

        const validOptions = options.filter(opt => opt.trim());
        if (validOptions.length < 2) {
            toast.error('Adicione pelo menos 2 opções válidas');
            return;
        }

        setIsSubmitting(true);
        try {
            await pollApi.createPoll(tableId, {
                question: question.trim(),
                options: validOptions,
                poll_type: 'single',
                is_anonymous: isAnonymous,
                show_results_live: showResultsLive
            });
            
            toast.success('Enquete iniciada!');
            onOpenChange(false);
            
            // Limpar form
            setQuestion('');
            setOptions(['', '']);
            setIsAnonymous(false);
            setShowResultsLive(true);
        } catch (error: any) {
            console.error('Erro ao criar enquete:', error);
            toast.error(error.message || 'Não foi possível criar a enquete');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="bg-game-secondary border-game-primary max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-game-primary flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Nova Enquete
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Pergunta */}
                    <div className="space-y-2">
                        <Label className="text-game-primary">Pergunta</Label>
                        <Input
                            placeholder="Digite a pergunta da enquete..."
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            className="bg-game-dark border-game-hover text-game-primary"
                            maxLength={200}
                        />
                    </div>

                    {/* Opções */}
                    <div className="space-y-3">
                        <Label className="text-game-primary">Opções de Resposta</Label>
                        {options.map((option, index) => (
                            <div key={index} className="flex gap-2">
                                <Input
                                    placeholder={`Opção ${index + 1}`}
                                    value={option}
                                    onChange={(e) => handleOptionChange(index, e.target.value)}
                                    className="bg-game-dark border-game-hover text-game-primary flex-1"
                                    maxLength={100}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveOption(index)}
                                    disabled={options.length <= 2}
                                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                        
                        {options.length < 10 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddOption}
                                className="w-full border-dashed border-game-hover text-game-muted hover:text-game-primary"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Adicionar Opção
                            </Button>
                        )}
                    </div>

                    {/* Configurações */}
                    <div className="space-y-4 pt-2 border-t border-game-hover">
                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label className="text-game-primary">Votação Anônima</Label>
                                <p className="text-xs text-game-muted">
                                    Não mostra quem votou em cada opção
                                </p>
                            </div>
                            <Switch
                                checked={isAnonymous}
                                onCheckedChange={setIsAnonymous}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label className="text-game-primary">Resultados em Tempo Real</Label>
                                <p className="text-xs text-game-muted">
                                    Mostra votos à medida que são registrados
                                </p>
                            </div>
                            <Switch
                                checked={showResultsLive}
                                onCheckedChange={setShowResultsLive}
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button 
                        variant="ghost" 
                        onClick={() => onOpenChange(false)} 
                        className="text-game-muted"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                        {isSubmitting ? 'Iniciando...' : 'Iniciar Enquete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
